import { useEffect, useState } from 'react';
import { api } from './api';
import { Drawer, Icon, LoadingScreen, PageError, StatusBadge } from './components';
import { AppAvatar, MetricTile, PanelHeading, WorkspaceTitle } from './app-shell-core';
import { Link, useParams } from './router';

function displayAddress(value: any): string {
  if (!value || typeof value !== 'object') return '';
  const locality = [value.city, value.state, value.postalCode].filter(Boolean).join(', ');
  return [value.line1, value.line2, locality].filter(Boolean).join(', ');
}

function displayPatientName(patient: any): string {
  if (!patient) return 'Patient';
  return `${patient.preferredName || patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Patient';
}

function durationMinutes(appointment: any): number {
  const start = new Date(appointment.startsAt);
  const end = new Date(appointment.endsAt || appointment.startsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}

function friendlyStatus(value?: string | null): string {
  const normalized = String(value || '').trim();
  if (!normalized) return 'Not checked';
  return normalized.toLowerCase().replaceAll('_', ' ').replace(/^./, (character) => character.toUpperCase());
}

export function AppointmentDetailPage() {
  const { organizationId = '', appointmentId = '' } = useParams();
  const [appointment, setAppointment] = useState<any>(null);
  const [insurance, setInsurance] = useState<any>(null);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setError('');
    void Promise.all([
      api<any>(`/organizations/${organizationId}/appointments/${appointmentId}`),
      api<any>(`/organizations/${organizationId}/insurance/appointments/${appointmentId}/readiness`).catch(() => null),
    ]).then(([result, insuranceResult]) => {
      if (!active) return;
      setAppointment(result.appointment);
      setInsurance(insuranceResult);
    }).catch((caught) => {
      if (active) setError(caught instanceof Error ? caught.message : 'Appointment could not load.');
    });
    return () => { active = false; };
  }, [organizationId, appointmentId, reloadKey]);

  if (error) return <PageError message={error} onRetry={() => { setAppointment(null); setReloadKey((value) => value + 1); }} />;
  if (!appointment) return <LoadingScreen />;

  const start = new Date(appointment.startsAt);
  const end = new Date(appointment.endsAt || appointment.startsAt);
  const patient = appointment.patient || {};
  const patientName = displayPatientName(patient);
  const provider = appointment.providerMembership;
  const providerName = provider ? `${provider.user.firstName} ${provider.user.lastName}${provider.providerProfile?.credentials ? `, ${provider.providerProfile.credentials}` : ''}` : 'Unassigned';
  const locationAddress = displayAddress(appointment.location?.address);
  const minutes = durationMinutes(appointment);

  return <>
    <WorkspaceTitle
      eyebrow={<span><Link to={`/organizations/${organizationId}/schedule`}>Appointments &amp; Calendar</Link> <Icon name="chevron-right" size={13} /> Appointment details</span>}
      title={<span className="patient-workspace-title-name appointment-detail-workspace-title"><AppAvatar name={patientName} identityKey={patient.id || appointment.id} size="lg" /><span>{patientName} <StatusBadge value={appointment.status || 'SCHEDULED'} /></span></span>}
      description={`${start.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} · ${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}–${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · ${appointment.serviceType?.name || 'Appointment'}`}
      actions={<div className="patient-workspace-actions">{patient.id && <Link className="button primary" to={`/organizations/${organizationId}/patients/${patient.id}`}><Icon name="patients" />Open patient</Link>}<Link className="button" to={`/organizations/${organizationId}/schedule?patientId=${patient.id || ''}`}><Icon name="calendar" />Open schedule</Link>{patient.id && <Link className="button" to={`/organizations/${organizationId}/patients/${patient.id}?tab=billing`}><Icon name="billing" />Billing</Link>}</div>}
    />

    <div className="app-metric-grid appointment-detail-summary-grid">
      <MetricTile icon="calendar-check" label="Date & time" value={start.toLocaleDateString([], { month: 'short', day: 'numeric' })} detail={`${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · ${minutes} min`} tone="info" />
      <MetricTile icon="providers" label="Provider" value={provider ? `${provider.user.firstName} ${provider.user.lastName}` : 'Unassigned'} detail={provider?.providerProfile?.specialty || provider?.providerProfile?.credentials || 'Provider assignment'} />
      <MetricTile icon="locations" label="Location" value={appointment.location?.name || 'Unassigned'} detail={appointment.location?.code || 'Practice location'} />
      <MetricTile icon="insurance" label="Insurance readiness" value={friendlyStatus(insurance?.readiness)} detail={insurance?.policy ? insurance.policy.payerName : 'No active policy'} tone={insurance?.readiness === 'READY' ? 'success' : 'warning'} />
    </div>

    <div className="appointment-detail-app-layout">
      <main className="appointment-detail-main-column">
        <section className="app-panel">
          <PanelHeading icon="calendar-check" title="Visit details" description="Scheduling and visit information for this appointment." />
          <div className="appointment-detail-facts">
            <div className="appointment-detail-fact"><span>Appointment type</span><strong>{appointment.serviceType?.name || '—'}</strong><small>{appointment.serviceType?.isTelehealth ? 'Telehealth visit' : 'In-person visit'}</small></div>
            <div className="appointment-detail-fact"><span>Status</span><strong>{friendlyStatus(appointment.status || 'SCHEDULED')}</strong><small>Current appointment workflow state</small></div>
            <div className="appointment-detail-fact"><span>Provider</span><strong>{providerName}</strong><small>{provider?.providerProfile?.specialty || provider?.user?.email || 'No additional provider details'}</small></div>
            <div className="appointment-detail-fact"><span>Location</span><strong>{appointment.location?.name || '—'}</strong><small>{locationAddress || appointment.location?.phone || 'No location details'}</small></div>
          </div>
        </section>
        <section className="app-panel">
          <PanelHeading icon="forms" title="Visit notes" description="Reason for visit and staff-only scheduling notes." />
          <div className="appointment-detail-notes">
            <div className="appointment-detail-note"><span>Reason for visit</span><p>{appointment.reason || 'No reason for visit was entered.'}</p></div>
            <div className="appointment-detail-note"><span>Internal notes</span><p>{appointment.internalNotes || 'No internal notes were entered.'}</p></div>
            {appointment.cancellationReason && <div className="appointment-detail-note"><span>Cancellation reason</span><p>{appointment.cancellationReason}</p></div>}
          </div>
        </section>
      </main>

      <aside className="appointment-detail-side-column">
        <section className="app-panel">
          <PanelHeading icon="patients" title="Patient" />
          <div className="appointment-detail-identity-card"><AppAvatar name={patientName} identityKey={patient.id || appointment.id} /><span><strong>{patientName}</strong><small>{patient.patientNumber || 'Patient record'}</small><small>{patient.dateOfBirth ? `DOB ${new Date(patient.dateOfBirth).toLocaleDateString()}` : 'DOB not on file'}</small></span></div>
          <div className="appointment-detail-side-list">{patient.phone && <div><span>Phone</span><strong>{patient.phone}</strong></div>}{patient.email && <div><span>Email</span><strong>{patient.email}</strong></div>}</div>
        </section>
        <section className="app-panel">
          <PanelHeading icon="insurance" title="Coverage at this visit" />
          <div className="appointment-detail-side-list"><div><span>Readiness</span><strong>{friendlyStatus(insurance?.readiness)}</strong></div><div><span>Policy</span><strong>{insurance?.policy?.payerName || 'No active policy'}</strong><small>{insurance?.policy?.memberId ? `Member ${insurance.policy.memberId}` : 'Review patient insurance if needed'}</small></div></div>
        </section>
        <section className="app-panel">
          <PanelHeading icon="activity" title="Next actions" />
          <div className="app-action-stack">{patient.id && <Link className="app-action-tile" to={`/organizations/${organizationId}/patients/${patient.id}?tab=intake`}><span className="app-action-tile-icon"><Icon name="clipboard-check" /></span><span><strong>Review intake &amp; forms</strong><small>Check patient paperwork status</small></span><Icon name="chevron-right" /></Link>}{patient.id && <Link className="app-action-tile" to={`/organizations/${organizationId}/patients/${patient.id}?tab=documents`}><span className="app-action-tile-icon"><Icon name="forms" /></span><span><strong>Patient documents</strong><small>Open completed forms and consents</small></span><Icon name="chevron-right" /></Link>}</div>
        </section>
      </aside>
    </div>
  </>;
}

export function AppointmentQuickView({ organizationId, appointment, onClose, onEdit }: { organizationId: string; appointment: any; onClose: () => void; onEdit?: (appointment: any) => void }) {
  const [details, setDetails] = useState<any>(appointment);
  const [insurance, setInsurance] = useState<any>(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!appointment?.id) return;
    let active = true;
    setDetails(appointment);
    setInsurance(null);
    setLoadError('');
    void Promise.all([
      api<any>(`/organizations/${organizationId}/appointments/${appointment.id}`),
      api<any>(`/organizations/${organizationId}/insurance/appointments/${appointment.id}/readiness`).catch(() => null),
    ]).then(([appointmentResult, insuranceResult]) => {
      if (!active) return;
      setDetails(appointmentResult?.appointment || appointment);
      setInsurance(insuranceResult);
    }).catch((caught) => {
      if (active) setLoadError(caught instanceof Error ? caught.message : 'Some appointment details could not be loaded.');
    });
    return () => { active = false; };
  }, [organizationId, appointment?.id]);

  if (!appointment) return null;
  const current = details || appointment;
  const start = new Date(current.startsAt);
  const end = new Date(current.endsAt || current.startsAt);
  const patient = current.patient || {};
  const patientName = displayPatientName(patient);
  const providerMembership = current.providerMembership;
  const provider = providerMembership?.user;
  const providerName = provider ? `${provider.firstName} ${provider.lastName}${providerMembership?.providerProfile?.credentials ? `, ${providerMembership.providerProfile.credentials}` : ''}` : 'Unassigned';
  const locationAddress = displayAddress(current.location?.address);
  const minutes = durationMinutes(current);
  const dateLabel = Number.isNaN(start.getTime()) ? 'Date unavailable' : start.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const timeLabel = Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) ? 'Time unavailable' : `${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}–${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  const readiness = insurance?.readiness || 'NOT_CHECKED';
  const patientDob = patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not on file';
  const hasVisitNotes = Boolean(current.reason || current.internalNotes || current.cancellationReason);

  const runEdit = () => {
    if (!onEdit) return;
    onClose();
    onEdit(current);
  };

  return <Drawer title="Appointment" icon="calendar-check" onClose={onClose} className="app-quickview-drawer appointment-quickview-drawer">
    <div className="appointment-quickview-shell">
      <section className="appointment-quickview-hero">
        <AppAvatar name={patientName} identityKey={patient.id || current.id} size="lg" />
        <div className="appointment-quickview-hero-copy">
          <div className="appointment-quickview-name-row"><h3>{patientName}</h3><StatusBadge value={current.status || 'SCHEDULED'} label={friendlyStatus(current.status || 'SCHEDULED')} /></div>
          <p>{dateLabel} · {timeLabel}</p>
          <div className="appointment-quickview-meta"><span>{current.serviceType?.name || 'Appointment'}</span>{minutes > 0 && <span>{minutes} min</span>}{patient.patientNumber && <span>{patient.patientNumber}</span>}</div>
        </div>
      </section>

      {loadError && <div className="appointment-quickview-warning"><Icon name="alert" /><span>{loadError}</span></div>}

      <div className="appointment-quickview-content">
        <section className="appointment-quickview-block">
          <div className="appointment-quickview-block-heading"><Icon name="calendar-check" /><h4>Visit details</h4></div>
          <div className="appointment-quickview-detail-grid">
            <div className="appointment-quickview-detail"><span>Provider</span><strong>{providerName}</strong><small>{providerMembership?.providerProfile?.specialty || 'Provider assignment'}</small></div>
            <div className="appointment-quickview-detail"><span>Location</span><strong>{current.location?.name || 'Unassigned'}</strong><small>{locationAddress || current.location?.phone || 'No location details'}</small></div>
            <div className="appointment-quickview-detail"><span>Appointment type</span><strong>{current.serviceType?.name || '—'}</strong><small>{current.serviceType?.isTelehealth ? 'Telehealth visit' : 'In-person visit'}</small></div>
            <div className="appointment-quickview-detail appointment-quickview-insurance-detail"><span>Insurance</span><div className="appointment-quickview-insurance-line"><strong>{insurance?.policy?.payerName || 'No active policy'}</strong><StatusBadge value={readiness} label={friendlyStatus(insurance?.readiness)} /></div><small>{insurance?.policy?.memberId ? `Member ${insurance.policy.memberId}` : 'Coverage readiness has not been confirmed.'}</small></div>
          </div>
        </section>

        <section className="appointment-quickview-block">
          <div className="appointment-quickview-block-heading"><Icon name="forms" /><h4>Visit notes</h4></div>
          {hasVisitNotes ? <div className="appointment-quickview-note-list">
            {current.reason && <div><span>Reason for visit</span><p>{current.reason}</p></div>}
            {current.internalNotes && <div><span>Internal notes</span><p>{current.internalNotes}</p></div>}
            {current.cancellationReason && <div><span>Cancellation reason</span><p>{current.cancellationReason}</p></div>}
          </div> : <p className="appointment-quickview-empty-note">No visit notes have been added.</p>}
        </section>

        <section className="appointment-quickview-block appointment-quickview-patient-block">
          <div className="appointment-quickview-block-heading"><Icon name="patients" /><h4>Patient details</h4></div>
          <div className="appointment-quickview-patient-grid">
            <div><span>Date of birth</span><strong>{patientDob}</strong></div>
            <div><span>Phone</span><strong>{patient.phone || 'Not on file'}</strong></div>
            <div className="appointment-quickview-patient-email"><span>Email</span><strong>{patient.email || 'Not on file'}</strong></div>
          </div>
        </section>
      </div>

      <footer className="appointment-quickview-actions">
        {onEdit && <button type="button" className="button primary" onClick={runEdit}><Icon name="edit" />Edit appointment</button>}
        {patient.id && <Link className="button" to={`/organizations/${organizationId}/patients/${patient.id}`} onClick={onClose}><Icon name="patients" />Open patient</Link>}
        <details className="app-action-menu appointment-quickview-more">
          <summary className="button appointment-quickview-more-button" aria-label="More appointment actions"><Icon name="more" />More</summary>
          <div className="app-action-menu-popover appointment-quickview-more-popover">
            <Link to={`/organizations/${organizationId}/schedule?patientId=${patient.id || ''}`} onClick={onClose}><Icon name="calendar" />Open schedule</Link>
            {patient.id && <Link to={`/organizations/${organizationId}/patients/${patient.id}?tab=billing`} onClick={onClose}><Icon name="billing" />Billing &amp; payments</Link>}
          </div>
        </details>
      </footer>
    </div>
  </Drawer>;
}

export const AppointmentDetailsModal = AppointmentQuickView;
